'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';

// ─── PRAYER TIME UTILITIES ────────────────────────────────────────────────────

export type PrayerState = 'pre-fajr' | 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

export function getPrayerState(): PrayerState {
  const h = new Date().getHours();
  if (h >= 0 && h < 5) return 'pre-fajr';
  if (h >= 5 && h < 7) return 'fajr';
  if (h >= 12 && h < 15) return 'dhuhr';
  if (h >= 15 && h < 18) return 'asr';
  if (h >= 18 && h < 20) return 'maghrib';
  return 'isha';
}

export function getPrayerColors(state: PrayerState): { bg: string; accent: string; sky: string } {
  const map: Record<PrayerState, { bg: string; accent: string; sky: string }> = {
    'pre-fajr': { bg: 'linear-gradient(180deg, #0a0520 0%, #060d12 100%)', accent: '#6b4fa0', sky: '#1a0d3a' },
    'fajr':     { bg: 'linear-gradient(180deg, #1a0a1a 0%, #2d1a2a 40%, #060d12 100%)', accent: '#c97a5a', sky: '#3d1a2a' },
    'dhuhr':    { bg: 'linear-gradient(180deg, #0d1a0a 0%, #060d12 100%)', accent: '#c9a84c', sky: '#1a2d0a' },
    'asr':      { bg: 'linear-gradient(180deg, #1a1200 0%, #060d12 100%)', accent: '#d4882a', sky: '#2d1e00' },
    'maghrib':  { bg: 'linear-gradient(180deg, #1a0800 0%, #2d0d00 30%, #060d12 100%)', accent: '#c94a2a', sky: '#3d1200' },
    'isha':     { bg: 'linear-gradient(180deg, #060d1a 0%, #060d12 100%)', accent: '#2d4a8a', sky: '#0a1020' },
  };
  return map[state];
}

export function getSeason(): 'spring' | 'summer' | 'fall' | 'winter' {
  const m = new Date().getMonth();
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'fall';
  return 'winter';
}

const PRAYER_LABELS: Record<PrayerState, string> = {
  'pre-fajr': 'Pre-Fajr',
  'fajr': 'Fajr',
  'dhuhr': 'Dhuhr',
  'asr': 'Asr',
  'maghrib': 'Maghrib',
  'isha': 'Isha',
};

// ─── CITY BIOME BACKGROUNDS ───────────────────────────────────────────────────

const CITY_BIOMES: Record<string, { label: string; biome: string; color: string }> = {
  chicago:   { label: 'Chicago', biome: 'Tallgrass Prairie', color: '#4a7a3a' },
  atlanta:   { label: 'Atlanta', biome: 'Piedmont Forest', color: '#2d6a2d' },
  portland:  { label: 'Portland', biome: 'Pacific Northwest Old Growth', color: '#1a5a3a' },
  detroit:   { label: 'Detroit', biome: 'Great Lakes Shoreline', color: '#2a4a6a' },
  durham:    { label: 'Durham', biome: 'Piedmont Longleaf Pine', color: '#5a6a2a' },
  louisville:{ label: 'Louisville', biome: 'Ohio River Valley', color: '#3a5a2a' },
  cincinnati:{ label: 'Cincinnati', biome: 'Appalachian Foothills', color: '#4a5a2a' },
  pittsburgh:{ label: 'Pittsburgh', biome: 'Allegheny Forest', color: '#3a4a2a' },
};

// ─── FIRST VISIT REVEAL ───────────────────────────────────────────────────────

export function FirstVisitReveal({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<'window' | 'line1' | 'line2' | 'dissolve'>('window');
  const prayerState = getPrayerState();
  const colors = getPrayerColors(prayerState);
  const season = getSeason();

  const cityKey = typeof window !== 'undefined' ? (localStorage.getItem('haal_city') || 'chicago')
    : 'chicago';
  const biome = CITY_BIOMES[cityKey] || CITY_BIOMES.chicago;

  const seasonPalette: Record<string, string> = {
    spring: 'rgba(80,160,80,0.12)',
    summer: 'rgba(60,140,60,0.18)',
    fall:   'rgba(180,100,40,0.14)',
    winter: 'rgba(80,100,140,0.10)',
  };

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('line1'), 800);
    const t2 = setTimeout(() => setPhase('line2'), 3300);
    const t3 = setTimeout(() => setPhase('dissolve'), 5200);
    const t4 = setTimeout(() => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('haal_garden_first_visit', 'true');
      }
      onComplete();
    }, 7000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [onComplete]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: colors.bg,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        transition: 'opacity 1.5s ease',
        opacity: phase === 'dissolve' ? 0 : 1,
        pointerEvents: phase === 'dissolve' ? 'none' : 'all',
      }}
    >
      {/* Atmospheric biome layer */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 50% 80%, ${seasonPalette[season]} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      {/* Horizon glow */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
        background: `linear-gradient(0deg, ${colors.sky}60 0%, transparent 100%)`,
        pointerEvents: 'none',
      }} />
      {/* City + biome label */}
      <div style={{
        position: 'absolute', bottom: 40, left: 0, right: 0,
        textAlign: 'center',
        opacity: phase === 'window' ? 0 : 0.4,
        transition: 'opacity 1.5s ease',
      }}>
        <p style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.accent }}>
          {biome.label} · {biome.biome} · {PRAYER_LABELS[prayerState]} · {season.charAt(0).toUpperCase() + season.slice(1)}
        </p>
      </div>
      {/* Main text */}
      <div style={{ textAlign: 'center', padding: '0 24px', maxWidth: 600, position: 'relative', zIndex: 1 }}>
        <p
          style={{
            fontFamily: "'Amiri', serif",
            fontSize: 'clamp(18px, 4vw, 26px)',
            color: '#c9a84c',
            lineHeight: 1.6,
            opacity: phase === 'window' ? 0 : 1,
            transition: 'opacity 1.5s ease',
            marginBottom: 24,
          }}
        >
          "Reflect on the perfect systems and design of creation around us."
        </p>
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 'clamp(11px, 2.5vw, 14px)',
            color: 'rgba(212,197,169,0.6)',
            letterSpacing: '0.05em',
            opacity: phase === 'line2' || phase === 'dissolve' ? 1 : 0,
            transition: 'opacity 1.5s ease',
          }}
        >
          You haven't started anything. You've arrived somewhere.
        </p>
      </div>
      {/* Skip */}
      <button
        onClick={() => {
          if (typeof window !== 'undefined') localStorage.setItem('haal_garden_first_visit', 'true');
          onComplete();
        }}
        style={{
          position: 'absolute', bottom: 80, right: 32,
          fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
          color: 'rgba(212,197,169,0.25)', background: 'transparent', border: 'none', cursor: 'pointer',
          opacity: phase === 'window' ? 0 : 0.6, transition: 'opacity 1s ease',
        }}
      >
        Enter garden →
      </button>
    </div>
  );
}

// ─── FITRAH SEED ──────────────────────────────────────────────────────────────

export function FitrahSeed() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      {/* Seed */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <button
          onClick={() => setDrawerOpen(true)}
          title="فطرة — Fitra: Your original design"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            padding: '12px 20px',
          }}
        >
          {/* SVG seedling */}
          <div style={{ position: 'relative', width: 56, height: 56 }}>
            {/* Pulse ring */}
            <div style={{
              position: 'absolute', inset: -8,
              borderRadius: '50%',
              border: '1px solid rgba(201,168,76,0.3)',
              animation: 'fitrahPulse 3s ease-in-out infinite',
            }} />
            <div style={{
              position: 'absolute', inset: -16,
              borderRadius: '50%',
              border: '1px solid rgba(201,168,76,0.12)',
              animation: 'fitrahPulse 3s ease-in-out infinite 1s',
            }} />
            {/* Seedling SVG */}
            <svg viewBox="0 0 56 56" width="56" height="56" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Glow */}
              <circle cx="28" cy="28" r="20" fill="rgba(201,168,76,0.08)" />
              {/* Stem */}
              <line x1="28" y1="44" x2="28" y2="22" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
              {/* Left leaf */}
              <path d="M28 30 C20 26 16 18 22 14 C26 12 28 18 28 22" fill="rgba(201,168,76,0.25)" stroke="#c9a84c" strokeWidth="1" strokeLinecap="round"/>
              {/* Right leaf */}
              <path d="M28 26 C36 22 40 14 34 10 C30 8 28 14 28 18" fill="rgba(201,168,76,0.2)" stroke="#c9a84c" strokeWidth="1" strokeLinecap="round"/>
              {/* Root hint */}
              <path d="M28 44 C24 46 22 50 26 52" stroke="rgba(201,168,76,0.3)" strokeWidth="1" strokeLinecap="round"/>
              <path d="M28 44 C32 46 34 50 30 52" stroke="rgba(201,168,76,0.3)" strokeWidth="1" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#c9a84c', direction: 'rtl' }}>فطرة</p>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginTop: 2 }}>Fitra</p>
          </div>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)', textAlign: 'center', maxWidth: 140 }}>
            Your original design. Already growing.
          </p>
        </button>
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(6,13,18,0.85)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
          onClick={() => setDrawerOpen(false)}
        >
          <div
            style={{
              background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: '16px 16px 0 0', padding: '32px 28px 40px',
              maxWidth: 560, width: '100%',
              borderTop: '2px solid rgba(201,168,76,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>فطرة — Fitra</p>
                <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)' }}>Original Design · Already Present</p>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>

            {/* Quran 30:30 */}
            <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(212,197,169,0.9)', direction: 'rtl', lineHeight: 1.8, marginBottom: 10, textAlign: 'right' }}>
                فَأَقِمْ وَجْهَكَ لِلدِّينِ حَنِيفًا ۚ فِطْرَتَ اللَّهِ الَّتِي فَطَرَ النَّاسَ عَلَيْهَا
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.6 }}>
                "So direct your face toward the religion, inclining to truth. [Adhere to] the fitrah of Allah upon which He has created [all] people."
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', marginTop: 8 }}>Quran 30:30</p>
            </div>

            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7, marginBottom: 16 }}>
              This is not a module you planted. It was placed before you arrived.
            </p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.7, marginBottom: 24 }}>
              The Fitrah is the proof that you aren't a spontaneous occurrence. Your capacity for recognition was designed into you before you chose to engage. The garden didn't create this seed — it named what was already growing.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link
                href="/keys-to-kulli-haal"
                style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                  padding: '8px 16px', borderRadius: 6,
                  background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
                  color: '#c9a84c', textDecoration: 'none',
                }}
              >
                Key 01 AYAT → Original Design
              </Link>
              <Link
                href="/sl-glossary"
                style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                  padding: '8px 16px', borderRadius: 6,
                  background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.25)',
                  color: '#2d7a47', textDecoration: 'none',
                }}
              >
                Fitra in SL Glossary →
              </Link>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fitrahPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.15); }
        }
      `}</style>
    </>
  );
}

// ─── COMMUNITY GARDEN CONTEXT ─────────────────────────────────────────────────

const ADJACENT_PLOTS = [
  { role: 'Inner Seeker', city: 'Chicago', cultivation: 0.75, position: { top: '8%', left: '2%' } },
  { role: 'Community Caretaker', city: 'Atlanta', cultivation: 0.9, position: { top: '5%', right: '3%' } },
  { role: 'Academic', city: 'Durham', cultivation: 0.55, position: { bottom: '10%', left: '1%' } },
  { role: 'Activist', city: 'Detroit', cultivation: 0.3, position: { bottom: '8%', right: '2%' } },
];

export function CommunityGardenContext({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ position: 'relative' }}>
      {/* Mustaqarrun label */}
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(201,168,76,0.45)', direction: 'rtl' }}>مُسْتَقَرٌّ</p>
        <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.25)' }}>Mustaqarrun · Your settled place</p>
      </div>

      {/* Adjacent plots (decorative) */}
      {ADJACENT_PLOTS.map((plot, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            ...plot.position,
            opacity: 0.18,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <div style={{
            border: '1px dashed rgba(201,168,76,0.4)',
            borderRadius: 8,
            padding: '6px 10px',
            background: `rgba(45,122,71,${plot.cultivation * 0.08})`,
            minWidth: 90,
          }}>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: '#c9a84c', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{plot.role}</p>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.5)' }}>{plot.city}</p>
            {/* Cultivation dots */}
            <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
              {Array.from({ length: 5 }).map((_, j) => (
                <div key={j} style={{
                  width: 4, height: 4, borderRadius: '50%',
                  background: j < Math.round(plot.cultivation * 5) ? 'rgba(201,168,76,0.6)' : 'rgba(255,255,255,0.1)',
                }} />
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Main plot content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}

// ─── TAFAKKUR MODE ────────────────────────────────────────────────────────────

const BRIDGE01_CARDS = [
  {
    icon: '◎',
    title: 'Creation as Classroom',
    arabic: 'الخلق كمدرسة',
    text: 'Every element of the environment is a teacher. The leaf, the hydrant, the vacant lot — each is an Ayah waiting to be read.',
  },
  {
    icon: '☽',
    title: 'Ayat in the Horizons',
    arabic: 'آيات في الآفاق',
    text: 'We will show them Our signs in the horizons and within themselves. (41:53) — The signs are not hidden. They are everywhere, always.',
  },
  {
    icon: '◈',
    title: 'Domain Expansion',
    arabic: 'توسع المجال',
    text: 'Awareness of the ever-present veil. The Batin is not behind the Zahir — it is the Zahir, seen more completely.',
  },
  {
    icon: '⬡',
    title: 'The Veil Becomes Visible',
    arabic: 'الحجاب يصبح مرئياً',
    text: 'The bridge is not a destination but a passage. You are not arriving somewhere new — you are seeing where you already are.',
  },
];

interface TafakkurModeProps {
  active: boolean;
  onExit: () => void;
  prayerState: PrayerState;
}

export function TafakkurMode({ active, onExit, prayerState }: TafakkurModeProps) {
  const colors = getPrayerColors(prayerState);

  if (!active) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(6,13,18,0.96)', backdropFilter: 'blur(12px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px',
        animation: 'tafakkurFadeIn 0.8s ease',
      }}
    >
      {/* Atmospheric background */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 50% 40%, ${colors.accent}08 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, width: '100%', textAlign: 'center' }}>
        {/* Arabic label */}
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: 'rgba(201,168,76,0.5)', marginBottom: 8 }}>تفكر</p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', marginBottom: 32 }}>Tafakkur · Stillness</p>

        {/* Fitrah seed pulse */}
        <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'center' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            border: '1px solid rgba(201,168,76,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fitrahPulse 4s ease-in-out infinite',
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#c9a84c', opacity: 0.7 }} />
          </div>
        </div>

        {/* Main text */}
        <p style={{
          fontFamily: "'Amiri', serif",
          fontSize: 'clamp(16px, 3.5vw, 22px)',
          color: 'rgba(212,197,169,0.8)',
          lineHeight: 1.7,
          marginBottom: 40,
        }}>
          "The perfect systems are always present. You only need to be still enough to see them."
        </p>

        {/* Bridge 01 concept cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 40 }}>
          {BRIDGE01_CARDS.map((card, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(201,168,76,0.1)',
                borderRadius: 10,
                padding: '16px 18px',
                textAlign: 'left',
                animation: `tafakkurCardFade 0.6s ease ${i * 0.15}s both`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ color: '#c9a84c', fontSize: 14 }}>{card.icon}</span>
                <p style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#F5F0E8' }}>{card.title}</p>
              </div>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(201,168,76,0.4)', marginBottom: 6, direction: 'rtl', textAlign: 'right' }}>{card.arabic}</p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>{card.text}</p>
            </div>
          ))}
        </div>

        {/* Return button */}
        <button
          onClick={onExit}
          style={{
            fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase',
            padding: '12px 28px', borderRadius: 8,
            background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)',
            color: '#c9a84c', cursor: 'pointer', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.12)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.06)'; }}
        >
          Return to Garden
        </button>
      </div>

      <style>{`
        @keyframes tafakkurFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes tafakkurCardFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fitrahPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}

// ─── TAFAKKUR TOGGLE BUTTON ───────────────────────────────────────────────────

export function TafakkurToggle({ onActivate }: { onActivate: () => void }) {
  return (
    <button
      onClick={onActivate}
      title="Enter Tafakkur — stillness mode"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
        padding: '7px 14px', borderRadius: 6,
        background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)',
        color: 'rgba(201,168,76,0.6)', cursor: 'pointer', transition: 'all 0.2s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.08)';
        (e.currentTarget as HTMLElement).style.color = '#c9a84c';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.04)';
        (e.currentTarget as HTMLElement).style.color = 'rgba(201,168,76,0.6)';
      }}
    >
      <span style={{ fontSize: 12 }}>☽</span>
      ◌ Tafakkur
      <span style={{ fontFamily: "'Amiri', serif", fontSize: 11 }}>تفكر</span>
    </button>
  );
}

// ─── NIYYAH FIELD ─────────────────────────────────────────────────────────────

export function NiyyahField() {
  const today = new Date().toDateString();
  const [niyyah, setNiyyah] = useState('');
  const [savedNiyyah, setSavedNiyyah] = useState<{ text: string; date: string } | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem('haal_daily_niyyah');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSavedNiyyah(parsed);
        if (parsed.date === today) setNiyyah(parsed.text);
      } catch {}
    }
  }, [today]);

  function handleSave() {
    const entry = { text: niyyah, date: today };
    localStorage.setItem('haal_daily_niyyah', JSON.stringify(entry));
    setSavedNiyyah(entry);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const isToday = savedNiyyah?.date === today;

  return (
    <div style={{
      background: 'rgba(201,168,76,0.03)',
      border: '1px solid rgba(201,168,76,0.12)',
      borderRadius: 10,
      padding: '16px 20px',
      marginBottom: 20,
    }}>
      <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 10 }}>
        نية · Niyyah — Your Intention Today
      </p>

      {isToday && savedNiyyah ? (
        <div>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.6, fontStyle: 'italic', marginBottom: 8 }}>
            "{savedNiyyah.text}"
          </p>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)' }}>
            Your niyyah from today — Still aligned?
          </p>
          <button
            onClick={() => { setSavedNiyyah(null); setNiyyah(''); }}
            style={{
              marginTop: 8, fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'rgba(212,197,169,0.3)', background: 'transparent', border: 'none', cursor: 'pointer',
            }}
          >
            Renew intention
          </button>
        </div>
      ) : (
        <div>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, marginBottom: 10 }}>
            What brings you to the garden today?
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <input
              type="text"
              value={niyyah}
              onChange={e => setNiyyah(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && niyyah.trim()) handleSave(); }}
              placeholder="Your intention... (Bismillah)"
              style={{
                flex: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6, padding: '8px 12px',
                fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.8)',
                outline: 'none',
              }}
            />
            <button
              onClick={handleSave}
              disabled={!niyyah.trim()}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
                padding: '8px 14px', borderRadius: 6,
                background: niyyah.trim() ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${niyyah.trim() ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.06)'}`,
                color: niyyah.trim() ? '#c9a84c' : 'rgba(212,197,169,0.2)',
                cursor: niyyah.trim() ? 'pointer' : 'default',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {saved ? '✓ Set' : 'Set'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SOIL PURITY AMBIENT ──────────────────────────────────────────────────────

export function getSoilPurityState(): 'rich' | 'neutral' | 'dry' {
  if (typeof window === 'undefined') return 'neutral';
  const stored = localStorage.getItem('haal_daily_niyyah');
  if (!stored) return 'dry';
  try {
    const parsed = JSON.parse(stored);
    const daysSince = (Date.now() - new Date(parsed.date).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince < 1) return 'rich';
    if (daysSince < 3) return 'neutral';
    return 'dry';
  } catch { return 'dry'; }
}

export function getSoilBackground(purity: 'rich' | 'neutral' | 'dry'): string {
  const map = {
    rich:    'radial-gradient(ellipse at 50% 100%, rgba(45,122,71,0.12) 0%, rgba(45,90,40,0.06) 40%, transparent 70%)',
    neutral: 'radial-gradient(ellipse at 50% 100%, rgba(80,80,60,0.08) 0%, transparent 60%)',
    dry:     'radial-gradient(ellipse at 50% 100%, rgba(100,90,70,0.06) 0%, transparent 60%)',
  };
  return map[purity];
}

// ─── FAJR DISCOVERY TOAST ─────────────────────────────────────────────────────

const PRAYER_DISCOVERY_MESSAGES: Record<PrayerState, { title: string; body: string }> = {
  'pre-fajr': {
    title: 'The garden is still.',
    body: 'Pre-Fajr. Creation holds its breath before the first light. The garden submits to the same stillness.',
  },
  'fajr': {
    title: 'The garden shifted. — This is Fajr.',
    body: 'The first light breaks. Creation marks its own time. The garden submits to the same schedule as the birds and the light.',
  },
  'dhuhr': {
    title: 'The garden shifted. — This is Dhuhr.',
    body: 'Full illumination. The sun at its zenith. Creation marks its own time — the garden submits to the same rhythm.',
  },
  'asr': {
    title: 'The garden shifted. — This is Asr.',
    body: 'The golden slant of afternoon. Al-Asr — time itself is the witness. The garden submits to the same schedule as the light.',
  },
  'maghrib': {
    title: 'The garden shifted. — This is Maghrib.',
    body: 'Creation marks its own time. The garden submits to the same schedule as the birds and the light.',
  },
  'isha': {
    title: 'The garden shifted. — This is Isha.',
    body: 'The deep blue-black of night. Creation rests. The garden submits to the same stillness as the stars.',
  },
};

export function FajrDiscoveryToast() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState<{ title: string; body: string } | null>(null);
  const lastStateRef = useRef<PrayerState | null>(null);

  useEffect(() => {
    function check() {
      const current = getPrayerState();
      if (lastStateRef.current && lastStateRef.current !== current) {
        // State changed — check if we've shown this toast before
        const key = `haal_prayer_toast_${current}`;
        if (typeof window !== 'undefined' && !localStorage.getItem(key)) {
          localStorage.setItem(key, 'true');
          setMessage(PRAYER_DISCOVERY_MESSAGES[current]);
          setVisible(true);
          setTimeout(() => setVisible(false), 8000);
        }
      }
      lastStateRef.current = current;
    }
    check();
    const interval = setInterval(check, 60000); // check every minute
    return () => clearInterval(interval);
  }, []);

  if (!visible || !message) return null;

  return (
    <div
      style={{
        position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        zIndex: 800,
        background: '#0a1218', border: '1px solid rgba(201,168,76,0.25)',
        borderRadius: 10, padding: '16px 20px',
        maxWidth: 400, width: 'calc(100vw - 48px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        animation: 'toastSlideUp 0.4s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: '#c9a84c', marginBottom: 6 }}>
            {message.title}
          </p>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6 }}>
            {message.body}
          </p>
        </div>
        <button
          onClick={() => setVisible(false)}
          style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}
        >×</button>
      </div>
      <style>{`
        @keyframes toastSlideUp { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
      `}</style>
    </div>
  );
}

// ─── MODULE ECOLOGY HELPERS ───────────────────────────────────────────────────

export function getModuleEcologyState(widgetId: string, history: Array<{ content_id: string; created_at: string }>): 'blooming' | 'active' | 'wilting' {
  const relevant = history.filter(h => h.content_id?.includes(widgetId));
  if (relevant.length === 0) return 'wilting';
  const latest = new Date(relevant[0].created_at);
  const daysSince = (Date.now() - latest.getTime()) / (1000 * 60 * 60 * 24);
  if (daysSince < 1) return 'blooming';
  if (daysSince < 7) return 'active';
  return 'wilting';
}

export function ModuleEcologyOverlay({ state }: { state: 'blooming' | 'active' | 'wilting' }) {
  if (state === 'active') return null;

  if (state === 'wilting') {
    return (
      <div
        title="This tool is waiting. Tend it."
        style={{
          position: 'absolute', top: 8, left: 8,
          fontSize: 10, opacity: 0.5,
          pointerEvents: 'none',
        }}
      >
        🍂
      </div>
    );
  }

  if (state === 'blooming') {
    return (
      <div
        style={{
          position: 'absolute', inset: 0, borderRadius: 'inherit',
          pointerEvents: 'none',
          animation: 'bloomShimmer 2s ease-in-out infinite',
          background: 'linear-gradient(135deg, rgba(201,168,76,0.06) 0%, transparent 50%, rgba(201,168,76,0.04) 100%)',
        }}
      >
        <style>{`
          @keyframes bloomShimmer {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return null;
}

// ─── OPEN LETTER QUOTE BAR ────────────────────────────────────────────────────

export function OpenLetterQuoteBar() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: 'rgba(201,168,76,0.03)',
      border: '1px solid rgba(201,168,76,0.12)',
      borderRadius: 8,
      marginBottom: 20,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: 'rgba(201,168,76,0.6)', direction: 'rtl' }}>لَسْنَا حَادِثَةً عَرَضِيَّة</span>
          <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)' }}>Open Letter · Plot 01</span>
        </div>
        <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 12, transition: 'transform 0.3s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(201,168,76,0.08)' }}>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7, marginTop: 12, marginBottom: 8 }}>
            "We aren't some spontaneous occurrence. Reflect on the perfect systems and design of creation around us."
          </p>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', marginBottom: 12 }}>
            Open Letter to Imprisoned Caretakers — Plot 01
          </p>
          <Link
            href="/open-letter"
            style={{
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
              color: '#c9a84c', textDecoration: 'none',
            }}
          >
            Read the full letter →
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── ATMOSPHERIC BACKGROUND LAYER ────────────────────────────────────────────

export function AtmosphericBackground({ prayerState, soilPurity }: { prayerState: PrayerState; soilPurity: 'rich' | 'neutral' | 'dry' }) {
  const colors = getPrayerColors(prayerState);
  const soilBg = getSoilBackground(soilPurity);

  return (
    <>
      {/* Prayer-time ambient */}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '100vh',
          pointerEvents: 'none', zIndex: 0,
          background: `radial-gradient(ellipse at 50% 0%, ${colors.accent}06 0%, transparent 60%)`,
          transition: 'background 3s ease',
        }}
      />
      {/* Soil purity ambient */}
      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: '50vh',
          pointerEvents: 'none', zIndex: 0,
          background: soilBg,
          transition: 'background 3s ease',
        }}
      />
    </>
  );
}

// ─── PROPOSITION 2: NON-REARRANGEABLE STRUCTURAL ELEMENTS ────────────────────

interface StructuralElementProps {
  type: 'alhamdulilah' | 'fajr' | 'mrg-bridge';
  alhamdulilahCount?: number;
  prayerState?: PrayerState;
}

export function StructuralElement({ type, alhamdulilahCount = 0, prayerState = 'isha' }: StructuralElementProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const colors = getPrayerColors(prayerState || 'isha');

  const LOCK_TOOLTIP = 'This is structural. You are within the design, not above it.';

  if (type === 'alhamdulilah') {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '20px 24px',
          background: 'rgba(201,168,76,0.04)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 12,
          textAlign: 'center',
        }}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        {/* Lock glyph */}
        <div style={{
          position: 'absolute', top: 10, right: 12,
          fontSize: 11, color: 'rgba(201,168,76,0.3)',
          cursor: 'help',
          transition: 'color 0.2s',
          color: tooltipVisible ? 'rgba(201,168,76,0.7)' : 'rgba(201,168,76,0.3)',
        }}>◈</div>

        {/* Tooltip */}
        {tooltipVisible && (
          <div style={{
            position: 'absolute', top: -44, left: '50%', transform: 'translateX(-50%)',
            background: '#0a1218', border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: 6, padding: '6px 12px',
            fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.7)',
            whiteSpace: 'nowrap', zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}>
            {LOCK_TOOLTIP}
          </div>
        )}

        <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', marginBottom: 4 }}>الحمد لله</p>
        <p style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 900, color: '#c9a84c', lineHeight: 1 }}>{alhamdulilahCount}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginTop: 6 }}>
          recognitions recorded
        </p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.25)', marginTop: 4, maxWidth: 200, lineHeight: 1.5 }}>
          This number measures your attention, not your value. Everything you've recognized was already present.
        </p>
      </div>
    );
  }

  if (type === 'fajr') {
    const pLabel = PRAYER_LABELS[prayerState];
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px',
          background: `${colors.accent}06`,
          border: `1px solid ${colors.accent}20`,
          borderRadius: 8,
        }}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        {tooltipVisible && (
          <div style={{
            position: 'absolute', top: -44, left: '50%', transform: 'translateX(-50%)',
            background: '#0a1218', border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: 6, padding: '6px 12px',
            fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.7)',
            whiteSpace: 'nowrap', zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}>
            {LOCK_TOOLTIP}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: colors.accent,
            boxShadow: `0 0 8px ${colors.accent}60`,
            animation: 'fajrPulse 3s ease-in-out infinite',
          }} />
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: colors.accent }}>{pLabel}</p>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', marginTop: 1 }}>Creation marks its own time</p>
          </div>
        </div>
        <span style={{ fontSize: 10, color: 'rgba(201,168,76,0.3)' }}>◈</span>
        <style>{`@keyframes fajrPulse { 0%,100%{opacity:1;} 50%{opacity:0.4;} }`}</style>
      </div>
    );
  }

  if (type === 'mrg-bridge') {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'rgba(45,122,71,0.05)',
          border: '1px solid rgba(45,122,71,0.2)',
          borderRadius: 8,
        }}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        {tooltipVisible && (
          <div style={{
            position: 'absolute', top: -44, left: '50%', transform: 'translateX(-50%)',
            background: '#0a1218', border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: 6, padding: '6px 12px',
            fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.7)',
            whiteSpace: 'nowrap', zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}>
            {LOCK_TOOLTIP}
          </div>
        )}
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#2d7a47' }}>Community Amanah</p>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(45,122,71,0.7)', direction: 'rtl', marginTop: 2 }}>المجتمع</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a
            href="/muslim-resource-guide"
            style={{
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '6px 12px', borderRadius: 6,
              background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)',
              color: '#2d7a47', textDecoration: 'none',
            }}
          >
            Open MRG →
          </a>
          <span style={{ fontSize: 10, color: 'rgba(45,122,71,0.4)' }}>◈</span>
        </div>
      </div>
    );
  }

  return null;
}

// ─── PROPOSITION 2: LINEAGE EMERGENCE SYSTEM ─────────────────────────────────

const LINEAGE_SIGNALS: Record<string, string> = {
  'irl-lens': 'forest-keeper',
  'observation-chain': 'forest-keeper',
  'ayat': 'forest-keeper',
  'mrg': 'restoration-warrior',
  'quests': 'restoration-warrior',
  'names': 'water-guardian',
  'names-reference': 'water-guardian',
  'saved': 'water-guardian',
  'framework-map': 'edge-walker',
  'keys': 'edge-walker',
  'sl-glossary': 'edge-walker',
};

const LINEAGE_DESCRIPTIONS = {
  'forest-keeper': {
    label: 'Forest Keeper',
    arabic: 'حارس الغابة',
    color: '#2d7a47',
    desc: 'You tend the living systems — ecological observation, sensory engagement with creation, reading Ayat in the natural world. The forest is your primary classroom.',
    signal: 'ecological observation',
  },
  'restoration-warrior': {
    label: 'Restoration Warrior',
    arabic: 'محارب الإصلاح',
    color: '#c94a2a',
    desc: 'You move toward what is broken and work to restore it. Community resources, justice work, Islah — you bridge the gap between what is and what the Mizan requires.',
    signal: 'resource-bridging and service',
  },
  'water-guardian': {
    label: 'Water Guardian',
    arabic: 'حارس الماء',
    color: '#2d8a87',
    desc: 'You carry knowledge and nourish others with it. Names study, deep reading, preservation of understanding — you are the channel through which recognition flows.',
    signal: 'Names study and deep reading',
  },
  'edge-walker': {
    label: 'Edge Walker',
    arabic: 'سالك الحافة',
    color: '#8a8ab8',
    desc: 'You move between worlds — connecting frameworks, bridging disciplines, mapping the edges where different kinds of knowing meet. You see the pattern in the pattern.',
    signal: 'framework-mapping and connection',
  },
};

interface LineageEmergenceProps {
  interactionLog: string[]; // widget IDs of first 3 interactions
  onLineageSet: (lineage: string) => void;
  currentLineage: string | null;
}

export function LineageEmergence({ interactionLog, onLineageSet, currentLineage }: LineageEmergenceProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFullDescriptions, setShowFullDescriptions] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [selectedLineage, setSelectedLineage] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // Compute suggested lineage from first 3 interactions
  const suggestedLineage = React.useMemo(() => {
    const counts: Record<string, number> = {};
    interactionLog.slice(0, 3).forEach(id => {
      const lineage = LINEAGE_SIGNALS[id];
      if (lineage) counts[lineage] = (counts[lineage] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort(([, a], [, b]) => b - a);
    return sorted[0]?.[0] || null;
  }, [interactionLog]);

  // Show drawer after 3 interactions if no lineage set
  useEffect(() => {
    if (interactionLog.length >= 3 && !currentLineage && suggestedLineage && !confirmed) {
      const key = 'haal_lineage_drawer_shown';
      if (typeof window !== 'undefined' && !localStorage.getItem(key)) {
        setTimeout(() => setDrawerOpen(true), 1200);
        localStorage.setItem(key, 'true');
      }
    }
  }, [interactionLog.length, currentLineage, suggestedLineage, confirmed]);

  if (!drawerOpen) return null;

  const suggested = suggestedLineage ? LINEAGE_DESCRIPTIONS[suggestedLineage as keyof typeof LINEAGE_DESCRIPTIONS] : null;

  function handleConfirm(lineage: string) {
    onLineageSet(lineage);
    setConfirmed(true);
    setDrawerOpen(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_lineage', lineage);
      localStorage.setItem('haal_lineage_reflection', reflectionText);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(6,13,18,0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={() => setDrawerOpen(false)}
    >
      <div
        style={{
          background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: '16px 16px 0 0', padding: '32px 28px 48px',
          maxWidth: 600, width: '100%',
          borderTop: '2px solid rgba(201,168,76,0.3)',
          maxHeight: '85vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
              Lineage Recognition
            </p>
            <h3 style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 900, textTransform: 'uppercase', color: '#F5F0E8' }}>
              Your first actions suggest a pattern
            </h3>
          </div>
          <button onClick={() => setDrawerOpen(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        {suggested && !showFullDescriptions && (
          <div>
            <div style={{
              background: `${suggested.color}08`,
              border: `1px solid ${suggested.color}30`,
              borderRadius: 10, padding: '20px 24px', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: suggested.color, flexShrink: 0 }} />
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: '#F5F0E8' }}>{suggested.label}</p>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${suggested.color}80`, direction: 'rtl' }}>{suggested.arabic}</p>
                </div>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7 }}>{suggested.desc}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', marginTop: 10 }}>
                Signal: your first actions centered on {suggested.signal}
              </p>
            </div>

            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6, marginBottom: 20 }}>
              Does this resonate?
            </p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <button
                onClick={() => handleConfirm(suggestedLineage!)}
                style={{
                  flex: 1, fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '12px 20px', borderRadius: 8,
                  background: `${suggested.color}15`, border: `1px solid ${suggested.color}40`,
                  color: suggested.color, cursor: 'pointer',
                }}
              >
                Yes — Recognized as {suggested.label}
              </button>
              <button
                onClick={() => setShowFullDescriptions(true)}
                style={{
                  fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '12px 20px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(212,197,169,0.5)', cursor: 'pointer',
                }}
              >
                Not quite
              </button>
            </div>
          </div>
        )}

        {showFullDescriptions && (
          <div>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6, marginBottom: 20 }}>
              Read each description. One will call to you — not because you chose it, but because it was already in you.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {Object.entries(LINEAGE_DESCRIPTIONS).map(([key, lineage]) => (
                <button
                  key={key}
                  onClick={() => setSelectedLineage(key)}
                  style={{
                    textAlign: 'left', padding: '16px 20px', borderRadius: 10,
                    background: selectedLineage === key ? `${lineage.color}12` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${selectedLineage === key ? lineage.color + '50' : 'rgba(255,255,255,0.08)'}`,
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: lineage.color, flexShrink: 0 }} />
                    <p style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#F5F0E8' }}>{lineage.label}</p>
                    <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${lineage.color}70` }}>{lineage.arabic}</p>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6 }}>{lineage.desc}</p>
                </button>
              ))}
            </div>

            {selectedLineage && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 8 }}>
                  What in this description calls to you?
                </p>
                <textarea
                  value={reflectionText}
                  onChange={e => setReflectionText(e.target.value)}
                  placeholder="A brief reflection..."
                  rows={3}
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8, padding: '10px 14px',
                    fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.8)',
                    outline: 'none', resize: 'none', boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={() => handleConfirm(selectedLineage)}
                  style={{
                    marginTop: 12, width: '100%',
                    fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                    padding: '12px 20px', borderRadius: 8,
                    background: `${LINEAGE_DESCRIPTIONS[selectedLineage as keyof typeof LINEAGE_DESCRIPTIONS].color}15`,
                    border: `1px solid ${LINEAGE_DESCRIPTIONS[selectedLineage as keyof typeof LINEAGE_DESCRIPTIONS].color}40`,
                    color: LINEAGE_DESCRIPTIONS[selectedLineage as keyof typeof LINEAGE_DESCRIPTIONS].color,
                    cursor: 'pointer',
                  }}
                >
                  Recognized as {LINEAGE_DESCRIPTIONS[selectedLineage as keyof typeof LINEAGE_DESCRIPTIONS].label}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PROPOSITION 2: ROOT CONNECTION TOAST ────────────────────────────────────

interface RootConnectionData {
  encounter: string;
  ward: string;
  matchCount: number;
  matchCity: string;
  sharedName: string;
  sharedNameAr: string;
}

interface RootConnectionToastProps {
  connection: RootConnectionData | null;
  onDismiss: () => void;
}

export function RootConnectionToast({ connection, onDismiss }: RootConnectionToastProps) {
  const [lineProgress, setLineProgress] = useState(0);

  useEffect(() => {
    if (!connection) return;
    const t = setTimeout(() => setLineProgress(100), 100);
    const dismiss = setTimeout(onDismiss, 12000);
    return () => { clearTimeout(t); clearTimeout(dismiss); };
  }, [connection, onDismiss]);

  if (!connection) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
      zIndex: 900,
      maxWidth: 440, width: 'calc(100vw - 48px)',
      animation: 'rootToastIn 0.5s ease',
    }}>
      {/* Animated root line */}
      <div style={{
        height: 1, marginBottom: 8,
        background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)',
        width: `${lineProgress}%`,
        transition: 'width 2s ease',
        opacity: 0.6,
      }} />
      <div style={{
        background: '#0a1218', border: '1px solid rgba(201,168,76,0.3)',
        borderRadius: 10, padding: '16px 20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#c9a84c', marginBottom: 8 }}>
              ◈ Root Connection
            </p>
            <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>
              Your observation of <strong style={{ color: '#F5F0E8' }}>{connection.encounter}</strong> in{' '}
              <strong style={{ color: '#F5F0E8' }}>{connection.ward}</strong> connects to{' '}
              <strong style={{ color: '#c9a84c' }}>{connection.matchCount} observations</strong> in{' '}
              {connection.matchCity}. They share the Name{' '}
              <strong style={{ color: '#c9a84c' }}>{connection.sharedName}</strong>{' '}
              <span style={{ fontFamily: "'Amiri', serif", fontSize: 13 }}>{connection.sharedNameAr}</span>.
            </p>
            <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, fontStyle: 'italic' }}>
              The roots run deeper than you planted.
            </p>
            <a
              href="/observation-chain-archive"
              style={{
                display: 'inline-block', marginTop: 10,
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: '#c9a84c', textDecoration: 'none',
              }}
            >
              See the connection →
            </a>
          </div>
          <button
            onClick={onDismiss}
            style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}
          >×</button>
        </div>
      </div>
      <style>{`
        @keyframes rootToastIn { from { opacity: 0; transform: translateX(-50%) translateY(16px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
      `}</style>
    </div>
  );
}

// ─── PROPOSITION 2: PRAYER-TIME MODULE GATE ──────────────────────────────────

const CONTEMPLATIVE_MODULES = ['irl-lens', 'names', 'saved', 'garment', 'keys', 'ayat', 'history'];
const SERVICE_MODULES = ['mrg', 'quests', 'ai-quests', 'observation-chain'];

const MAGHRIB_AYAT = [
  { arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ', ref: '41:53', translation: 'We will show them Our signs in the horizons and within themselves.' },
  { arabic: 'وَفِي أَنفُسِكُمْ ۚ أَفَلَا تُبْصِرُونَ', ref: '51:21', translation: 'And in yourselves. Then will you not see?' },
  { arabic: 'أَلَمْ يَرَوْا إِلَى الطَّيْرِ مُسَخَّرَاتٍ فِي جَوِّ السَّمَاءِ', ref: '67:19', translation: 'Do they not see the birds controlled in the atmosphere of the sky?' },
  { arabic: 'هُوَ الْأَوَّلُ وَالْآخِرُ وَالظَّاهِرُ وَالْبَاطِنُ', ref: '57:3', translation: 'He is the First and the Last, the Evident and the Hidden.' },
  { arabic: 'فِطْرَتَ اللَّهِ الَّتِي فَطَرَ النَّاسَ عَلَيْهَا', ref: '30:30', translation: 'The fitrah of Allah upon which He has created all people.' },
];

interface PrayerTimeGateProps {
  widgetId: string;
  prayerState: PrayerState;
  gardenVisitCount: number;
  children: React.ReactNode;
}

export function PrayerTimeGate({ widgetId, prayerState, gardenVisitCount, children }: PrayerTimeGateProps) {
  // Only activate after 3+ visits
  if (gardenVisitCount < 3) return <>{children}</>;

  const isServiceModule = SERVICE_MODULES.includes(widgetId);
  const isContemplativeModule = CONTEMPLATIVE_MODULES.includes(widgetId);
  const isPreFajrOrFajr = prayerState === 'pre-fajr' || prayerState === 'fajr';
  const isAsr = prayerState === 'asr';

  // Pre-Fajr/Fajr: service modules go dormant
  if (isPreFajrOrFajr && isServiceModule) {
    return (
      <div style={{ padding: '16px', opacity: 0.5, filter: 'saturate(0.3)' }}>
        <div style={{ pointerEvents: 'none' }}>{children}</div>
        <p style={{
          fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'rgba(212,197,169,0.4)', marginTop: 10, fontStyle: 'italic',
          borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8,
        }}>
          This work will be here at sunrise.
        </p>
      </div>
    );
  }

  // Asr: service modules get gold border pulse
  if (isAsr && isServiceModule) {
    return (
      <div style={{
        border: '1px solid rgba(201,168,76,0.4)',
        borderRadius: 'inherit',
        animation: 'asrPulse 3s ease-in-out infinite',
      }}>
        {children}
        <style>{`@keyframes asrPulse { 0%,100%{box-shadow:0 0 0 0 rgba(201,168,76,0);} 50%{box-shadow:0 0 12px 2px rgba(201,168,76,0.15);} }`}</style>
      </div>
    );
  }

  return <>{children}</>;
}

// ─── PROPOSITION 2: MAGHRIB AYAH OVERLAY ─────────────────────────────────────

export function MaghribAyahOverlay({ prayerState, gardenVisitCount }: { prayerState: PrayerState; gardenVisitCount: number }) {
  const [visible, setVisible] = useState(false);
  const [ayahIndex] = useState(() => Math.floor(Math.random() * MAGHRIB_AYAT.length));
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (prayerState === 'maghrib' && gardenVisitCount >= 3 && !dismissed) {
      const key = `haal_maghrib_ayah_${new Date().toDateString()}`;
      if (typeof window !== 'undefined' && !localStorage.getItem(key)) {
        setVisible(true);
        localStorage.setItem(key, 'true');
        setTimeout(() => setVisible(false), 180000); // 3 minutes
      }
    }
  }, [prayerState, gardenVisitCount, dismissed]);

  if (!visible) return null;

  const ayah = MAGHRIB_AYAT[ayahIndex];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 400,
      background: 'rgba(6,13,18,0.92)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      animation: 'maghribFadeIn 1.5s ease',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 500 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 32 }}>
          Maghrib · المغرب
        </p>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(20px, 5vw, 28px)', color: '#c9a84c', direction: 'rtl', lineHeight: 1.8, marginBottom: 20 }}>
          {ayah.arabic}
        </p>
        <p style={{ fontFamily: 'monospace', fontSize: 13, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, marginBottom: 8 }}>
          {ayah.translation}
        </p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', marginBottom: 40 }}>
          Quran {ayah.ref}
        </p>
        <button
          onClick={() => { setVisible(false); setDismissed(true); }}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
            padding: '10px 24px', borderRadius: 8,
            background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)',
            color: 'rgba(201,168,76,0.6)', cursor: 'pointer',
          }}
        >
          Return to Garden
        </button>
      </div>
      <style>{`@keyframes maghribFadeIn { from{opacity:0;} to{opacity:1;} }`}</style>
    </div>
  );
}

// ─── PROPOSITION 2: THREE-STAGE LANGUAGE ─────────────────────────────────────

export type GardenStage = 'aware' | 'conscious' | 'acting';

export function getGardenStage(alhamdulilahCount: number): GardenStage {
  if (alhamdulilahCount < 25) return 'aware';
  if (alhamdulilahCount < 100) return 'conscious';
  return 'acting';
}

const STAGE_DEFINITIONS = {
  aware: {
    label: 'AWARE',
    arabic: 'وَاعٍ',
    yaqeen: 'Ilm Yaqeen',
    yaqeenAr: 'عِلْمُ الْيَقِين',
    yaqeenDesc: 'Certainty of knowledge — you have encountered the signs',
    color: '#2d7a47',
    stages: 'Stages 1–2 · Witness, Exploration',
  },
  conscious: {
    label: 'CONSCIOUS',
    arabic: 'وَاعٍ بِعُمْق',
    yaqeen: 'Ayn Yaqeen',
    yaqeenAr: 'عَيْنُ الْيَقِين',
    yaqeenDesc: 'Certainty of sight — you have seen the Names at work',
    color: '#c9a84c',
    stages: 'Stages 3–5 · Understanding, Mastery, Depth',
  },
  acting: {
    label: 'ACTING',
    arabic: 'عَامِل',
    yaqeen: 'Haqq Yaqeen',
    yaqeenAr: 'حَقُّ الْيَقِين',
    yaqeenDesc: 'Certainty of experience — you have lived the recognition',
    color: '#8a8ab8',
    stages: 'Stages 6–7 · Service, Transmission',
  },
};

export function ThreeStageProgressBar({ alhamdulilahCount }: { alhamdulilahCount: number }) {
  const currentStage = getGardenStage(alhamdulilahCount);
  const stages: GardenStage[] = ['aware', 'conscious', 'acting'];

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10, padding: '16px 20px',
      marginBottom: 16,
    }}>
      <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', marginBottom: 14 }}>
        Recognition Journey
      </p>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {stages.map((stage, i) => {
          const def = STAGE_DEFINITIONS[stage];
          const isActive = stage === currentStage;
          const isPast = stages.indexOf(currentStage) > i;
          return (
            <div
              key={stage}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                background: isActive ? `${def.color}12` : isPast ? `${def.color}06` : 'rgba(255,255,255,0.01)',
                border: `1px solid ${isActive ? def.color + '50' : isPast ? def.color + '25' : 'rgba(255,255,255,0.05)'}`,
                boxShadow: isActive ? `0 0 16px ${def.color}15` : 'none',
                transition: 'all 0.3s',
              }}
            >
              <p style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: isActive ? def.color : isPast ? `${def.color}60` : 'rgba(212,197,169,0.2)', marginBottom: 4 }}>
                {def.label}
              </p>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: isActive ? `${def.color}80` : 'rgba(212,197,169,0.15)', direction: 'rtl' }}>
                {def.arabic}
              </p>
              {isActive && (
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', marginTop: 6, lineHeight: 1.4 }}>
                  {def.stages}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {/* Yaqeen triad */}
      <div style={{ display: 'flex', gap: 4 }}>
        {stages.map((stage) => {
          const def = STAGE_DEFINITIONS[stage];
          const isActive = stage === currentStage;
          return (
            <div key={stage} style={{ flex: 1, textAlign: 'center' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 7, textTransform: 'uppercase', letterSpacing: '0.08em', color: isActive ? def.color : 'rgba(212,197,169,0.2)' }}>
                {def.yaqeen}
              </p>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 9, color: isActive ? `${def.color}60` : 'rgba(212,197,169,0.1)' }}>
                {def.yaqeenAr}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PROPOSITION 2: DHIKR NOTIFICATION SYSTEM ────────────────────────────────

const DHIKR_VERSES = [
  { arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ', ref: '41:53', translation: 'We will show them Our signs in the horizons and within themselves.' },
  { arabic: 'فِطْرَتَ اللَّهِ الَّتِي فَطَرَ النَّاسَ عَلَيْهَا', ref: '30:30', translation: 'The fitrah of Allah upon which He has created all people.' },
  { arabic: 'هُوَ الْأَوَّلُ وَالْآخِرُ وَالظَّاهِرُ وَالْبَاطِنُ', ref: '57:3', translation: 'He is the First and the Last, the Evident and the Hidden.' },
  { arabic: 'وَفِي أَنفُسِكُمْ ۚ أَفَلَا تُبْصِرُونَ', ref: '51:21', translation: 'And in yourselves. Then will you not see?' },
  { arabic: 'يَا بَنِي آدَمَ قَدْ أَنزَلْنَا عَلَيْكُمْ لِبَاسًا يُوَارِي سَوْآتِكُمْ وَرِيشًا', ref: '7:26', translation: 'O children of Adam, We have bestowed upon you clothing to conceal your private parts and as adornment.' },
];

const DHIKR_MESSAGES = [
  'Subhanallah — glory to the One who created pairs in all things.',
  'The garden is patient. Have you been observing creation with your eyes? If so, the lens is working.',
  'Alhamdulillah ala kulli haal — all praise in every state.',
  'The signs are in the horizons and within yourselves.',
  'You are within the design. The design is the way of life.',
];

interface DhikrNotificationProps {
  dhikrMode: boolean;
  trigger: 'quest-reminder' | 'cohort-update' | 'return-prompt' | null;
  onDismiss: () => void;
}

export function DhikrNotification({ dhikrMode, trigger, onDismiss }: DhikrNotificationProps) {
  const [verseIndex] = useState(() => Math.floor(Math.random() * DHIKR_VERSES.length));
  const [msgIndex] = useState(() => Math.floor(Math.random() * DHIKR_MESSAGES.length));

  if (!trigger) return null;

  if (!dhikrMode) {
    // Standard notifications (when dhikr mode is off)
    const standardMessages: Record<string, string> = {
      'quest-reminder': 'You have active quests awaiting.',
      'cohort-update': 'Your cohort has been active this week.',
      'return-prompt': 'Welcome back to the garden.',
    };
    return (
      <div style={{
        position: 'fixed', bottom: 32, right: 24, zIndex: 700,
        background: '#0a1218', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10, padding: '14px 18px', maxWidth: 300,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        animation: 'dhikrIn 0.3s ease',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12 }}>{standardMessages[trigger]}</p>
          <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: 'pointer', fontSize: 14 }}>×</button>
        </div>
        <style>{`@keyframes dhikrIn { from{opacity:0;transform:translateY(8px);} to{opacity:1;transform:translateY(0);} }`}</style>
      </div>
    );
  }

  // Dhikr mode notifications
  const verse = DHIKR_VERSES[verseIndex];
  const isReturnPrompt = trigger === 'return-prompt';

  return (
    <div style={{
      position: 'fixed', bottom: 32, right: 24, zIndex: 700,
      background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10, padding: '18px 20px', maxWidth: 340,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      animation: 'dhikrIn 0.4s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          {isReturnPrompt ? (
            <>
              <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>
                {DHIKR_MESSAGES[msgIndex]}
              </p>
            </>
          ) : (
            <>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#c9a84c', direction: 'rtl', lineHeight: 1.7, marginBottom: 8 }}>
                {verse.arabic}
              </p>
              <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6, marginBottom: 4 }}>
                {verse.translation}
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>Quran {verse.ref}</p>
            </>
          )}
        </div>
        <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>×</button>
      </div>
      <style>{`@keyframes dhikrIn { from{opacity:0;transform:translateY(8px);} to{opacity:1;transform:translateY(0);} }`}</style>
    </div>
  );
}

// ─── PROPOSITION 3: PRIMARY NAME PER MODULE ───────────────────────────────────

export const MODULE_PRIMARY_NAMES: Record<string, { name: string; arabic: string; meaning: string; nameRef: string }> = {
  'irl-lens': { name: 'Al-Basir', arabic: 'البصير', meaning: 'The All-Seeing', nameRef: 'al-basir' },
  'mrg': { name: 'Ar-Razzaq', arabic: 'الرزاق', meaning: 'The Ever-Provider', nameRef: 'ar-razzaq' },
  'names': { name: 'Al-Alim', arabic: 'العليم', meaning: 'The All-Knowing', nameRef: 'al-alim' },
  'names-reference': { name: 'Al-Alim', arabic: 'العليم', meaning: 'The All-Knowing', nameRef: 'al-alim' },
  'saved': { name: 'Al-Khabir', arabic: 'الخبير', meaning: 'The All-Aware', nameRef: 'al-khabir' },
  'quests': { name: 'Al-Wakil', arabic: 'الوكيل', meaning: 'The Trustee', nameRef: 'al-wakil' },
  'ai-quests': { name: 'Al-Wakil', arabic: 'الوكيل', meaning: 'The Trustee', nameRef: 'al-wakil' },
  'garment': { name: 'Al-Muhaymin', arabic: 'المهيمن', meaning: 'The Guardian', nameRef: 'al-muhaymin' },
  'observation-chain': { name: 'Ash-Shahid', arabic: 'الشهيد', meaning: 'The Witness', nameRef: 'ash-shahid' },
  'framework-map': { name: 'Al-Hakim', arabic: 'الحكيم', meaning: 'The All-Wise', nameRef: 'al-hakim' },
  'hawas-dock': { name: 'As-Sami', arabic: 'السميع البصير', meaning: 'The All-Hearing, All-Seeing', nameRef: 'as-sami' },
  'keys': { name: 'Al-Hadi', arabic: 'الهادي', meaning: 'The Guide', nameRef: 'al-hadi' },
  'ayat': { name: 'Al-Hadi', arabic: 'الهادي', meaning: 'The Guide', nameRef: 'al-hadi' },
  'history': { name: 'Ash-Shahid', arabic: 'الشهيد', meaning: 'The Witness', nameRef: 'ash-shahid' },
  'badges': { name: 'Al-Karim', arabic: 'الكريم', meaning: 'The Generous', nameRef: 'al-karim' },
  'notes': { name: 'Al-Khabir', arabic: 'الخبير', meaning: 'The All-Aware', nameRef: 'al-khabir' },
};

interface ModuleNameWatermarkProps {
  widgetId: string;
}

export function ModuleNameWatermark({ widgetId }: ModuleNameWatermarkProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nameData = MODULE_PRIMARY_NAMES[widgetId];

  if (!nameData) return null;

  return (
    <>
      {/* Watermark on tile */}
      <div
        style={{
          position: 'absolute', bottom: 8, right: 10,
          pointerEvents: 'auto',
          cursor: 'pointer',
          zIndex: 2,
        }}
        onClick={e => { e.stopPropagation(); setDrawerOpen(true); }}
        title={`${nameData.name} — ${nameData.meaning}`}
      >
        <p style={{
          fontFamily: "'Amiri', serif",
          fontSize: 18,
          color: 'rgba(201,168,76,0.12)',
          direction: 'rtl',
          lineHeight: 1,
          transition: 'color 0.2s',
          userSelect: 'none',
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(201,168,76,0.35)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(201,168,76,0.12)'; }}
        >
          {nameData.arabic}
        </p>
      </div>

      {/* Micro-drawer */}
      {drawerOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 800,
            background: 'rgba(6,13,18,0.8)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
          onClick={() => setDrawerOpen(false)}
        >
          <div
            style={{
              background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: '14px 14px 0 0', padding: '28px 24px 40px',
              maxWidth: 480, width: '100%',
              borderTop: '2px solid rgba(201,168,76,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 26, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>{nameData.arabic}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: '#F5F0E8' }}>{nameData.name}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.5)', marginTop: 2 }}>{nameData.meaning}</p>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
            <div style={{
              background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)',
              borderRadius: 8, padding: '14px 16px', marginBottom: 16,
            }}>
              <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.7 }}>
                This module operates under <strong style={{ color: '#c9a84c' }}>{nameData.name}</strong>. Every interaction here is a borrowed echo of the attribute that belongs absolutely to Allah. The module cultivates a trace — the highest essence remains with The One.
              </p>
            </div>
            <a
              href={`/names-reference#${nameData.nameRef}`}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                padding: '8px 16px', borderRadius: 6,
                background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
                color: '#c9a84c', textDecoration: 'none', display: 'inline-block',
              }}
            >
              Full Names Reference entry →
            </a>
          </div>
        </div>
      )}
    </>
  );
}

// ─── PROPOSITION 3: BEING / AWARENESS ZONE LABELS ────────────────────────────

export const BEING_MODULES = ['irl-lens', 'mrg', 'quests', 'ai-quests', 'observation-chain'];
export const AWARENESS_MODULES = ['names', 'names-reference', 'saved', 'keys', 'ayat', 'framework-map', 'garment', 'history'];

export function BeingAwarenessZoneLabel({ zone }: { zone: 'being' | 'awareness' }) {
  if (zone === 'being') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '6px 0 10px',
        borderBottom: '1px solid rgba(45,122,71,0.15)',
        marginBottom: 12,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2d7a47', flexShrink: 0 }} />
        <div>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(45,122,71,0.7)', direction: 'rtl' }}>عَمَل</p>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)' }}>Action · Being · Production</p>
        </div>
        <div style={{ flex: 1, height: 1, background: 'rgba(45,122,71,0.1)' }} />
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Al-Khaliq axis</p>
      </div>
    );
  }
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '6px 0 10px',
      borderBottom: '1px solid rgba(138,138,184,0.15)',
      marginBottom: 12,
    }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#8a8ab8', flexShrink: 0 }} />
      <div>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(138,138,184,0.7)', direction: 'rtl' }}>إِدْرَاك</p>
        <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)' }}>Perception · Awareness</p>
      </div>
      <div style={{ flex: 1, height: 1, background: 'rgba(138,138,184,0.1)' }} />
      <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(138,138,184,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Al-Alim axis</p>
    </div>
  );
}

// ─── PROPOSITION 3: MODULE DEPTH → OPEN LETTER PASSAGE ───────────────────────

const MODULE_LETTER_PASSAGES: Record<string, { passage: string; arabic?: string; label: string; section: string }> = {
  'irl-lens': {
    label: 'Where this tool was born',
    section: 'Section 11 — The Fission Reactor',
    passage: 'Imagine a fission reactor that deconstructs anything it perceives into relevant metaphysical aspects — extracting the Names of Allah from every phenomenon it encounters. The IRL Lens is that reactor. Every observation is a fission sequence.',
  },
  'mrg': {
    label: 'Where this tool was born',
    section: 'Section — Al-Qadir / Resource Redistribution',
    passage: 'What reforms and resource redistribution propositions does someone that is formerly incarcerated have? The MRG is the answer to that question made navigable — a map of provision for those the system has displaced.',
  },
  'keys': {
    label: 'Where this tool was born',
    section: 'Section — Ihdini / Guidance',
    passage: 'Ihdini — guide me. The Keys are not a curriculum you complete. They are a guidance you receive. Each Key is a door that opens onto a wider recognition of what was always present.',
  },
  'names': {
    label: 'Where this tool was born',
    section: 'Section — The 99 Names',
    passage: 'Allah has ninety-nine Names — one hundred minus one — and whoever preserves them will enter Paradise. The Names Reference is not a catalog. It is a field of recognition. Every Name you encounter is a trace of the attribute that belongs absolutely to The One.',
    arabic: 'إِنَّ لِلَّهِ تِسْعَةً وَتِسْعِينَ اسْمًا مِائَةً إِلَّا وَاحِدًا مَنْ أَحْصَاهَا دَخَلَ الْجَنَّةَ',
  },
  'garment': {
    label: 'Where this tool was born',
    section: 'Section — Reshat Al-Taqwah · Quran 7:26',
    passage: 'O children of Adam, We have bestowed upon you clothing to conceal your private parts and as adornment. But the clothing of righteousness — that is best. That is from the signs of Allah that perhaps they will remember.',
    arabic: 'وَلِبَاسُ التَّقْوَىٰ ذَٰلِكَ خَيْرٌ',
  },
};

interface ModuleDepthPassageProps {
  widgetId: string;
  interactionCount: number; // bookmarks + observations + quests for this module
}

export function ModuleDepthPassage({ widgetId, interactionCount }: ModuleDepthPassageProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const passage = MODULE_LETTER_PASSAGES[widgetId];

  if (!passage || interactionCount < 3) return null;

  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); setDrawerOpen(true); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'rgba(201,168,76,0.5)', background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '4px 0',
          transition: 'color 0.2s',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#c9a84c'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(201,168,76,0.5)'; }}
      >
        <span>◈</span> Letter Root
      </button>

      {drawerOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 800,
            background: 'rgba(6,13,18,0.85)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
          onClick={() => setDrawerOpen(false)}
        >
          <div
            style={{
              background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: '14px 14px 0 0', padding: '28px 24px 44px',
              maxWidth: 560, width: '100%',
              borderTop: '2px solid rgba(201,168,76,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 4 }}>
                  ◈ {passage.label}
                </p>
                <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.5)' }}>{passage.section}</p>
              </div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>

            {passage.arabic && (
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#c9a84c', direction: 'rtl', lineHeight: 1.8, marginBottom: 16, textAlign: 'right' }}>
                {passage.arabic}
              </p>
            )}

            <div style={{
              background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)',
              borderRadius: 8, padding: '16px 20px', marginBottom: 20,
              borderLeft: '3px solid rgba(201,168,76,0.3)',
            }}>
              <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.8, fontStyle: 'italic' }}>
                "{passage.passage}"
              </p>
            </div>

            <a
              href="/open-letter"
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                padding: '8px 16px', borderRadius: 6,
                background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
                color: '#c9a84c', textDecoration: 'none', display: 'inline-block',
              }}
            >
              Read the Open Letter →
            </a>
          </div>
        </div>
      )}
    </>
  );
}

// ─── PROPOSITION 3: NAMES ATMOSPHERE ─────────────────────────────────────────

export interface ActiveNameAtmosphere {
  name: string;
  moduleEffects: Record<string, string>; // widgetId -> CSS class or style hint
}

export function getActiveNameAtmospheres(topNames: string[]): string[] {
  const atmosphericNames = ['ar-razzaq', 'al-hafiz', 'al-adl', 'al-basir', 'al-muhaymin'];
  return topNames.filter(n => atmosphericNames.includes(n.toLowerCase().replace(/\s/g, '-')));
}

export function getNamesAtmosphereStyle(widgetId: string, activeNames: string[]): React.CSSProperties {
  const styles: React.CSSProperties = {};
  const lowerNames = activeNames.map(n => n.toLowerCase());

  if (widgetId === 'mrg' && lowerNames.includes('ar-razzaq')) {
    styles.boxShadow = '0 0 20px rgba(201,168,76,0.08) inset';
    styles.borderColor = 'rgba(201,168,76,0.3)';
  }
  if (widgetId === 'garment' && lowerNames.includes('al-muhaymin')) {
    styles.boxShadow = '0 0 16px rgba(138,138,184,0.1) inset';
  }
  if (widgetId === 'irl-lens' && lowerNames.includes('al-basir')) {
    styles.transform = 'scale(1.01)';
  }

  return styles;
}

// ─── PROPOSITION 3: HUMILITY SURFACING ───────────────────────────────────────

interface HumilitySurfaceProps {
  namesExtracted: number;
  visible: boolean;
  onDismiss: () => void;
}

export function HumilitySurface({ namesExtracted, visible, onDismiss }: HumilitySurfaceProps) {
  useEffect(() => {
    if (visible) {
      const t = setTimeout(onDismiss, 6000);
      return () => clearTimeout(t);
    }
  }, [visible, onDismiss]);

  if (!visible) return null;

  return (
    <div style={{
      padding: '10px 14px',
      background: 'rgba(201,168,76,0.04)',
      border: '1px solid rgba(201,168,76,0.12)',
      borderRadius: 8,
      marginTop: 8,
      animation: 'humilityFade 0.4s ease',
    }}>
      <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6 }}>
        You have recognized <strong style={{ color: '#c9a84c' }}>{namesExtracted} Names</strong> in this encounter.{' '}
        <span style={{ fontFamily: "'Amiri', serif", fontSize: 13 }}>الله الخبير</span> — Allah is Al-Khabir, All-Aware of what you have seen and what remains hidden.
      </p>
      <style>{`@keyframes humilityFade { from{opacity:0;transform:translateY(4px);} to{opacity:1;transform:translateY(0);} }`}</style>
    </div>
  );
}

// ─── PROPOSITION 4: SEASONAL MODULE CYCLES ───────────────────────────────────
// "Commanded to come down for a time as Caretakers, to use this lowest, nearest
//  realm as a temporary place of provision, a tool." — Mata principle

export type ModuleSeasonState = 'fruiting' | 'active' | 'dormant' | 'harvest-ready' | 'shed';

const ECOLOGICAL_MODULES = ['irl-lens', 'observation-chain', 'ayat'];

export function getModuleSeasonState(
  widgetId: string,
  lastInteractionDate: string | null,
  questCompleted?: boolean
): ModuleSeasonState {
  const season = getSeason();
  const now = Date.now();
  const daysSince = lastInteractionDate
    ? (now - new Date(lastInteractionDate).getTime()) / (1000 * 60 * 60 * 24)
    : 999;

  if (questCompleted) return 'harvest-ready';
  if (daysSince > 30) return 'shed';
  if (daysSince > 14) return 'dormant';

  const isEcological = ECOLOGICAL_MODULES.includes(widgetId);
  const isWinter = season === 'winter';
  if (isEcological && isWinter && daysSince > 7) return 'dormant';

  if (daysSince <= 7) {
    const isSeasonalMatch =
      (season === 'spring' || season === 'summer') && isEcological;
    if (isSeasonalMatch || daysSince <= 2) return 'fruiting';
  }

  return 'active';
}

interface ModuleSeasonOverlayProps {
  state: ModuleSeasonState;
  widgetId: string;
}

export function ModuleSeasonOverlay({ state, widgetId }: ModuleSeasonOverlayProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const season = getSeason();
  const isWinter = season === 'winter';
  const isEcological = ECOLOGICAL_MODULES.includes(widgetId);

  if (state === 'active') return null;

  const overlayStyles: Record<ModuleSeasonState, React.CSSProperties> = {
    fruiting: {
      position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
      border: '1px solid rgba(201,168,76,0.4)',
      animation: 'fruitingShimmer 2.5s ease-in-out infinite',
      background: 'linear-gradient(135deg, rgba(201,168,76,0.06) 0%, transparent 60%)',
    },
    dormant: {
      position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
      filter: 'saturate(0.4)',
      background: 'rgba(0,0,0,0.15)',
    },
    'harvest-ready': {
      position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
      border: '1px solid rgba(201,168,76,0.5)',
      animation: 'harvestPulse 2s ease-in-out infinite',
    },
    shed: {
      position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
      border: '1px dashed rgba(212,197,169,0.15)',
      opacity: 0.4,
    },
    active: {},
  };

  const glyphs: Record<ModuleSeasonState, string> = {
    fruiting: '◉',
    dormant: '⌇',
    'harvest-ready': '◉',
    shed: '⌇',
    active: '',
  };

  const tooltips: Record<ModuleSeasonState, string> = {
    fruiting: 'Fruiting — active in this season.',
    dormant: isWinter && isEcological
      ? 'As-Sabur — The Patient. The root system is alive beneath the frost.' :'Dormant — still alive underground. The roots hold.',
    'harvest-ready': 'This cycle is complete. What will you carry forward?',
    shed: 'This tool has served its season. Return when it is needed again.',
    active: '',
  };

  const glyphColors: Record<ModuleSeasonState, string> = {
    fruiting: '#c9a84c',
    dormant: 'rgba(212,197,169,0.4)',
    'harvest-ready': '#c9a84c',
    shed: 'rgba(212,197,169,0.25)',
    active: '',
  };

  return (
    <>
      <div style={overlayStyles[state]} />
      {/* Corner glyph */}
      <div
        style={{
          position: 'absolute', top: 8, right: 8, zIndex: 3,
          cursor: 'help', pointerEvents: 'auto',
        }}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <span style={{ fontSize: 11, color: glyphColors[state], opacity: 0.8 }}>
          {glyphs[state]}
        </span>
        {tooltipVisible && (
          <div style={{
            position: 'absolute', top: -40, right: 0,
            background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 6, padding: '6px 12px',
            fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.7)',
            whiteSpace: 'nowrap', zIndex: 200,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            maxWidth: 260, whiteSpace: 'normal',
          }}>
            {tooltips[state]}
          </div>
        )}
      </div>
      {/* Harvest-ready prompt */}
      {state === 'harvest-ready' && (
        <div style={{
          position: 'absolute', bottom: 8, left: 0, right: 0,
          textAlign: 'center', pointerEvents: 'none', zIndex: 3,
        }}>
          <p style={{
            fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'rgba(201,168,76,0.6)',
            animation: 'harvestPromptFade 3s ease-in-out infinite',
          }}>
            This cycle is complete. What will you carry forward?
          </p>
        </div>
      )}
      {/* Winter dormancy — Big Bluestem underground */}
      {state === 'dormant' && isWinter && isEcological && (
        <div style={{
          position: 'absolute', bottom: 8, left: 10, right: 10,
          pointerEvents: 'none', zIndex: 3,
        }}>
          <p style={{
            fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)',
            lineHeight: 1.4,
          }}>
            ⌇ As-Sabur — The Patient. The root system is alive beneath the frost.
          </p>
        </div>
      )}
      <style>{`
        @keyframes fruitingShimmer {
          0%, 100% { opacity: 0.5; box-shadow: 0 0 0 0 rgba(201,168,76,0); }
          50% { opacity: 1; box-shadow: 0 0 12px 2px rgba(201,168,76,0.12); }
        }
        @keyframes harvestPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0); }
          50% { box-shadow: 0 0 16px 4px rgba(201,168,76,0.15); }
        }
        @keyframes harvestPromptFade {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </>
  );
}

// ─── PROPOSITION 4: LONGLEAF PINE NOTE (Garment tab) ─────────────────────────

export function LongleafPineNote({ garmentThreadCount }: { garmentThreadCount: number }) {
  if (garmentThreadCount > 3) return null;
  return (
    <div style={{
      background: 'rgba(45,122,71,0.05)',
      border: '1px solid rgba(45,122,71,0.15)',
      borderRadius: 8, padding: '14px 18px',
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ color: 'rgba(45,122,71,0.6)', fontSize: 14, flexShrink: 0 }}>⌇</span>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)', marginBottom: 6 }}>
            Longleaf Pine — Grass Stage
          </p>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.7 }}>
            Five years underground before the canopy. What you can't yet see is the foundation. The garment appears sparse — this is the Grass Stage. The roots are building what the eye cannot yet measure.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── PROPOSITION 4: ALHAMDULILAH HARVEST MODAL ───────────────────────────────
// "The counter should reset — not to zero, but to a new cycle. Each cycle is a harvest."

interface HarvestCycleSummary {
  topModules: string[];
  topNames: string[];
  strongestObservation?: string;
  cycleNumber: number;
  totalCumulative: number;
}

interface HarvestModalProps {
  visible: boolean;
  summary: HarvestCycleSummary;
  onBeginNextCycle: () => void;
}

export function HarvestModal({ visible, summary, onBeginNextCycle }: HarvestModalProps) {
  const [phase, setPhase] = useState<'reveal' | 'summary' | 'ready'>('reveal');

  useEffect(() => {
    if (!visible) { setPhase('reveal'); return; }
    const t1 = setTimeout(() => setPhase('summary'), 1800);
    const t2 = setTimeout(() => setPhase('ready'), 3600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(6,13,18,0.97)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
      animation: 'harvestFadeIn 1.2s ease',
    }}>
      {/* Grain texture overlay */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
        opacity: 0.6,
      }} />
      {/* Gold light radial */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 50% 40%, rgba(201,168,76,0.08) 0%, transparent 65%)',
        animation: 'harvestGlow 4s ease-in-out infinite',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 560, width: '100%', textAlign: 'center' }}>
        {/* Arabic */}
        <p style={{
          fontFamily: "'Amiri', serif",
          fontSize: 'clamp(28px, 6vw, 42px)',
          color: '#c9a84c',
          direction: 'rtl',
          lineHeight: 1.5,
          marginBottom: 12,
          opacity: phase === 'reveal' ? 0 : 1,
          transition: 'opacity 1.2s ease',
        }}>
          الحَمْدُ لِلَّه
        </p>
        <p style={{
          fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'rgba(201,168,76,0.6)', marginBottom: 32,
          opacity: phase === 'reveal' ? 0 : 1,
          transition: 'opacity 1.2s ease 0.3s',
        }}>
          A cycle complete
        </p>

        {/* Harvest message */}
        <div style={{
          background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)',
          borderRadius: 12, padding: '24px 28px', marginBottom: 28,
          opacity: phase === 'reveal' ? 0 : 1,
          transition: 'opacity 1s ease',
        }}>
          <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 14, lineHeight: 1.8, marginBottom: 20 }}>
            What grew in this cycle becomes seed for the next. Nothing is lost. Everything is transformed.
          </p>

          {/* Cycle summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {summary.topModules.length > 0 && (
              <div style={{
                background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, padding: '12px 14px', textAlign: 'left',
              }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 8 }}>
                  Modules most tended
                </p>
                {summary.topModules.slice(0, 3).map((m, i) => (
                  <p key={i} style={{ color: 'rgba(212,197,169,0.65)', fontSize: 11, lineHeight: 1.6 }}>
                    {i + 1}. {m}
                  </p>
                ))}
              </div>
            )}
            {summary.topNames.length > 0 && (
              <div style={{
                background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8, padding: '12px 14px', textAlign: 'left',
              }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 8 }}>
                  Names most active
                </p>
                {summary.topNames.slice(0, 3).map((n, i) => (
                  <p key={i} style={{ color: '#c9a84c', fontSize: 11, lineHeight: 1.6 }}>
                    {i + 1}. {n}
                  </p>
                ))}
              </div>
            )}
          </div>

          {summary.strongestObservation && (
            <div style={{
              background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 8, padding: '12px 16px', textAlign: 'left', marginBottom: 16,
            }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 6 }}>
                Strongest fruit of this cycle
              </p>
              <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, lineHeight: 1.6, fontStyle: 'italic' }}>
                "{summary.strongestObservation}"
              </p>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)' }}>
              Cycle {summary.cycleNumber} complete · {summary.totalCumulative} total recognitions
            </p>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={onBeginNextCycle}
          disabled={phase !== 'ready'}
          style={{
            fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase',
            padding: '14px 32px', borderRadius: 8,
            background: phase === 'ready' ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${phase === 'ready' ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.06)'}`,
            color: phase === 'ready' ? '#c9a84c' : 'rgba(212,197,169,0.2)',
            cursor: phase === 'ready' ? 'pointer' : 'default',
            transition: 'all 0.4s ease',
          }}
        >
          Begin the next cycle →
        </button>
      </div>

      <style>{`
        @keyframes harvestFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes harvestGlow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── PROPOSITION 4: IHSAN QUALITY THRESHOLD CHECK ────────────────────────────
// "An observation should not automatically increment the Alhamdulilah counter."

export interface ObservationQualityCheck {
  niyyahSet: boolean;
  layersCompleted: number; // of 8
  namesAnnotated: number; // names with annotation text, not just selected
  haqqCompleted: boolean;
}

export function checkIhsanThreshold(check: ObservationQualityCheck): {
  passes: boolean;
  missingItems: string[];
  completedItems: string[];
} {
  const missing: string[] = [];
  const completed: string[] = [];

  if (check.niyyahSet) completed.push("Niyyah set");
  else missing.push("Set today's garden niyyah");

  if (check.layersCompleted >= 3) completed.push(`${check.layersCompleted} of 8 layers completed`);
  else missing.push(`Complete at least 3 of 8 IRL Lens layers (${check.layersCompleted} done)`);

  if (check.namesAnnotated >= 2) completed.push(`${check.namesAnnotated} Names annotated`);
  else missing.push(`Annotate at least 2 Names with reflection (${check.namesAnnotated} done)`);

  if (check.haqqCompleted) completed.push("Haqq declaration written");
  else missing.push("Complete the Haqq declaration field");

  return {
    passes: missing.length === 0,
    missingItems: missing,
    completedItems: completed,
  };
}

interface IhsanQualityGateProps {
  qualityCheck: ObservationQualityCheck;
  onDismiss: () => void;
  visible: boolean;
}

export function IhsanQualityGate({ qualityCheck, onDismiss, visible }: IhsanQualityGateProps) {
  const result = checkIhsanThreshold(qualityCheck);

  if (!visible) return null;

  if (result.passes) {
    return (
      <div style={{
        position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        zIndex: 800, maxWidth: 400, width: 'calc(100vw - 48px)',
        background: '#0a1218', border: '1px solid rgba(201,168,76,0.3)',
        borderRadius: 10, padding: '16px 20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        animation: 'ihsanSlideUp 0.4s ease',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#c9a84c', marginBottom: 6, direction: 'rtl' }}>
              الحمد لله
            </p>
            <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, lineHeight: 1.6 }}>
              Recognition recorded.
            </p>
          </div>
          <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
        <style>{`@keyframes ihsanSlideUp { from{opacity:0;transform:translateX(-50%) translateY(12px);} to{opacity:1;transform:translateX(-50%) translateY(0);} }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      padding: '14px 18px',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 10, marginTop: 12,
      animation: 'ihsanFade 0.3s ease',
    }}>
      <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.7, marginBottom: 12 }}>
        This observation is recorded. Return to complete the remaining layers — the Alhamdulilah counts when the recognition runs deep.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {result.completedItems.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#2d7a47', fontSize: 10 }}>✓</span>
            <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.5)' }}>{item}</p>
          </div>
        ))}
        {result.missingItems.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10 }}>◌</span>
            <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.35)' }}>{item}</p>
          </div>
        ))}
      </div>
      <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic' }}>
        There's no expiration on a recognition in progress.
      </p>
      <style>{`@keyframes ihsanFade { from{opacity:0;transform:translateY(4px);} to{opacity:1;transform:translateY(0);} }`}</style>
    </div>
  );
}

// ─── PROPOSITION 4: INCOMPLETE OBSERVATION GLYPH ─────────────────────────────

interface IncompleteObservationCardProps {
  observationTitle: string;
  layersCompleted: number;
  onReturn: () => void;
}

export function IncompleteObservationCard({ observationTitle, layersCompleted, onReturn }: IncompleteObservationCardProps) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 8, padding: '14px 18px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      {/* Open circle glyph */}
      <span style={{ color: 'rgba(212,197,169,0.4)', fontSize: 16, flexShrink: 0, marginTop: 2 }}>◌</span>
      <div style={{ flex: 1 }}>
        <p style={{ color: '#F5F0E8', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{observationTitle}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.45)', marginBottom: 8 }}>
          Recognition in progress — {layersCompleted}of 8 layers complete
        </p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', fontStyle: 'italic', marginBottom: 10 }}>
          Waiting when you return.
        </p>
        <button
          onClick={onReturn}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '6px 14px', borderRadius: 6,
            background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.25)',
            color: '#2d8a87', cursor: 'pointer', transition: 'all 0.2s',
          }}
        >
          Return to deepen →
        </button>
      </div>
    </div>
  );
}

// ─── PROPOSITION 4: GRADUATED WITHDRAWAL SYSTEM ──────────────────────────────
// "As the caretaker progresses, the garden's prompts reduce."

export type PromptDensity = 'full' | 'reduced' | 'minimal';

export function getPromptDensity(gardenStage: GardenStage): PromptDensity {
  if (gardenStage === 'aware') return 'full';
  if (gardenStage === 'conscious') return 'reduced';
  return 'minimal';
}

interface GraduatedPromptProps {
  density: PromptDensity;
  fullPrompt: string;
  reducedPrompt: string;
  minimalPrompt?: string;
  className?: string;
}

export function GraduatedPrompt({ density, fullPrompt, reducedPrompt, minimalPrompt }: GraduatedPromptProps) {
  if (density === 'minimal') {
    if (!minimalPrompt) return null;
    return (
      <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
        {minimalPrompt}
      </p>
    );
  }
  if (density === 'reduced') {
    return (
      <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.6 }}>
        {reducedPrompt}
      </p>
    );
  }
  return (
    <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7 }}>
      {fullPrompt}
    </p>
  );
}

// Acting stage footer line
export function ActingStageFooter({ gardenStage }: { gardenStage: GardenStage }) {
  if (gardenStage !== 'acting') return null;
  return (
    <div style={{
      textAlign: 'center', padding: '16px 24px',
      borderTop: '1px solid rgba(255,255,255,0.04)',
      marginTop: 24,
    }}>
      <p style={{
        fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
        color: 'rgba(212,197,169,0.25)',
      }}>
        The garden succeeds when you no longer need it.
      </p>
    </div>
  );
}

// ─── PROPOSITION 4: DECLARATIVE COMMAND REGISTER ─────────────────────────────
// "The garden's UI copy should never be purely invitational."

interface WardAmanahBannerProps {
  wardName: string;
  disparityPercent: number;
  disparityType: string; // e.g. "food desert"
  linkedName: string; // e.g. "Ar-Razzaq"
  href: string;
}

export function WardAmanahBanner({ wardName, disparityPercent, disparityType, linkedName, href }: WardAmanahBannerProps) {
  const isHighDisparity = disparityPercent >= 80;

  if (!isHighDisparity) {
    return (
      <a href={href} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderRadius: 8,
        background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)',
        color: '#2d7a47', textDecoration: 'none',
        fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.08em',
        transition: 'all 0.2s',
      }}>
        <span>Explore {wardName} →</span>
      </a>
    );
  }

  return (
    <a href={href} style={{
      display: 'block', padding: '12px 16px', borderRadius: 8,
      background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)',
      textDecoration: 'none', transition: 'all 0.2s',
    }}>
      <p style={{ color: '#c9a84c', fontSize: 12, lineHeight: 1.6, marginBottom: 4 }}>
        {wardName} has {disparityPercent}% {disparityType}. The Amanah of {linkedName} requires a response.
      </p>
      <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)' }}>
        Bridge this recognition to action →
      </p>
    </a>
  );
}

// Declarative observation prompt
export function DeclarativeObservationPrompt({ density }: { density: PromptDensity }) {
  return (
    <GraduatedPrompt
      density={density}
      fullPrompt="This is an Ayah. Catalog it. Apply the Three Questions to what you observed. Extract the Names. Identify the Sunnat Allah."
      reducedPrompt="This is an Ayah. Catalog it."
      minimalPrompt={undefined}
    />
  );
}

// Declarative Amanah field prompt
export function AmanahFieldPrompt({ isEmpty, density }: { isEmpty: boolean; density: PromptDensity }) {
  if (!isEmpty) return null;
  return (
    <p style={{
      fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.08em',
      color: 'rgba(201,168,76,0.5)', marginTop: 6, lineHeight: 1.5,
    }}>
      The Amanah is unidentified. What does this recognition require of you?
    </p>
  );
}

// Empty garden first-visit prompt (declarative)
export function EmptyGardenPrompt({ hasFirstVisitCompleted }: { hasFirstVisitCompleted: boolean }) {
  if (!hasFirstVisitCompleted) return null;
  return (
    <div style={{
      textAlign: 'center', padding: '32px 24px',
      border: '1px dashed rgba(201,168,76,0.15)',
      borderRadius: 12, marginBottom: 20,
    }}>
      <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(201,168,76,0.6)', marginBottom: 12 }}>
        Your ward needs a caretaker.
      </p>
      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
        Begin with what you can see.
      </p>
      <a href="/inner-reality-lens" style={{
        fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
        padding: '10px 20px', borderRadius: 6,
        background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.3)',
        color: '#2d8a87', textDecoration: 'none', display: 'inline-block',
      }}>
        Open the IRL Lens →
      </a>
    </div>
  );
}

// ─── PROPOSITION 4: SCREWDRIVER TEST TOOLTIP ─────────────────────────────────
// "Every visual choice passes the screwdriver test: is this serving the work, or adorning the tool?"

export function ScrewdriverTestTooltip({ label, purpose }: { label: string; purpose: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', cursor: 'help' }}>
        {label}
      </span>
      {visible && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 6,
          background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 6, padding: '8px 12px',
          fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.6)',
          whiteSpace: 'nowrap', zIndex: 300,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          <span style={{ color: 'rgba(201,168,76,0.5)' }}>Purpose: </span>{purpose}
        </div>
      )}
    </div>
  );
}

// First-time prayer shift orientation tooltip
export function PrayerOrientationTooltip({ prayerState, onDismiss }: { prayerState: PrayerState; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = `haal_prayer_orientation_shown`;
    if (!localStorage.getItem(key)) {
      setVisible(true);
      localStorage.setItem(key, 'true');
      const t = setTimeout(() => setVisible(false), 8000);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const prayerNames: Record<PrayerState, string> = {
    'pre-fajr': 'Pre-Fajr', fajr: 'Fajr', dhuhr: 'Dhuhr',
    asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha',
  };

  return (
    <div style={{
      position: 'fixed', top: 80, right: 24, zIndex: 700,
      background: '#0a1218', border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10, padding: '14px 18px', maxWidth: 280,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      animation: 'dhikrIn 0.4s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.6 }}>
          This is <strong style={{ color: '#c9a84c' }}>{prayerNames[prayerState]}</strong>. The garden orients to creation's own schedule.
        </p>
        <button onClick={() => { setVisible(false); onDismiss(); }} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: 'pointer', fontSize: 14 }}>×</button>
      </div>
    </div>
  );
}

// ─── PROPOSITION 4: COMMUNITY GATING ─────────────────────────────────────────
// "Submit first. Then share." — Those who submit to revelation call to it.

interface CommunityGateCheckResult {
  hasQualifyingObservation: boolean;
  observationCount: number;
}

export async function checkCommunityGate(userId: string | null, sessionId: string): Promise<CommunityGateCheckResult> {
  // This is called from the community page — returns gate status
  // The actual Supabase query is done in the component that uses this
  return { hasQualifyingObservation: false, observationCount: 0 };
}

interface FeedGateBannerProps {
  onCatalogAyah: () => void;
}

export function FeedGateBanner({ onCatalogAyah }: FeedGateBannerProps) {
  return (
    <div style={{
      background: 'rgba(201,168,76,0.04)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 12, padding: '28px 24px',
      textAlign: 'center',
    }}>
      <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: 'rgba(201,168,76,0.6)', marginBottom: 12, direction: 'rtl' }}>
        الَّذِينَ يُسَلِّمُونَ لِلْوَحْيِ يَدْعُونَ إِلَيْهِ
      </p>
      <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.8, marginBottom: 8, maxWidth: 480, margin: '0 auto 16px' }}>
        The Feed is composed of submitted recognitions. Complete your first full observation to contribute.
      </p>
      <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12, lineHeight: 1.6, marginBottom: 20, maxWidth: 440, margin: '0 auto 20px' }}>
        Submit first. Then share. Those who submit to revelation call to it.
      </p>
      <button
        onClick={onCatalogAyah}
        style={{
          fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
          padding: '12px 24px', borderRadius: 8,
          background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.35)',
          color: '#2d8a87', cursor: 'pointer', transition: 'all 0.2s',
        }}
      >
        Catalog your first Ayah →
      </button>
    </div>
  );
}

const HarvestCycleSummary: React.FC = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: HarvestCycleSummary is not implemented yet.');
  }, []);
  return (
    <div>
      {/* HarvestCycleSummary placeholder */}
    </div>
  );
};

export { HarvestCycleSummary };